import {API_BASE_URL} from '../config/apiConfig';
import type {Contract, ContractListItem} from '../types/contract';

interface ContractsByBranchResult {
    success: boolean;
    contracts?: ContractListItem[];
    totalRecords?: number;
    error?: string;
}

interface UpdateContractResult {
    success: boolean;
    error?: string;
    violations?: string[];
}

export const getContractsByBranch = async (token: string, branchId: string): Promise<ContractsByBranchResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/branch/get?branchId=${encodeURIComponent(branchId)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {success: false, error: errorData.message || 'Failed to fetch contracts'};
        }

        const data = await response.json();
        return {success: true, contracts: data.contracts ?? [], totalRecords: data.totalRecords ?? 0};
    } catch (error) {
        console.error('getContractsByBranch error:', error);
        return {success: false, error: 'Failed to fetch contracts'};
    }
};

export const downloadContractDocument = async (
    token: string,
    contractId: string,
    format: 'pdf' | 'word',
): Promise<{success: boolean; blob?: Blob; fileName?: string; error?: string}> => {
    try {
        const response = await fetch(
            `${API_BASE_URL}/api/contract/${encodeURIComponent(contractId)}/download?format=${format}`,
            {method: 'GET', headers: {Authorization: `Bearer ${token}`}},
        );
        if (!response.ok) {
            return {success: false, error: 'Failed to download contract'};
        }
        const disposition = response.headers.get('Content-Disposition') ?? '';
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        const fileName = match?.[1] ?? `kontrate.${format === 'pdf' ? 'pdf' : 'docx'}`;
        return {success: true, blob: await response.blob(), fileName};
    } catch (error) {
        console.error('downloadContractDocument error:', error);
        return {success: false, error: 'Failed to download contract'};
    }
};

export interface ContractPreviewResult {
    success: boolean;
    blob?: Blob;
    documentId?: string;
    hash?: string;
    signed?: boolean;
    status?: number;
}

const readPreview = async (response: Response): Promise<ContractPreviewResult> => {
    if (!response.ok) {
        return {success: false, status: response.status};
    }
    return {
        success: true,
        blob: await response.blob(),
        documentId: response.headers.get('X-Document-Id') ?? undefined,
        hash: response.headers.get('X-Document-Hash') ?? undefined,
        signed: response.headers.get('X-Document-Signed') === 'true',
    };
};

export const previewContract = async (token: string, contractId: string): Promise<ContractPreviewResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/${encodeURIComponent(contractId)}/preview`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${token}`},
        });
        return await readPreview(response);
    } catch (error) {
        console.error('previewContract error:', error);
        return {success: false};
    }
};

export const signContract = async (
    token: string,
    contractId: string,
    documentId: string,
    hash: string,
): Promise<ContractPreviewResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/${encodeURIComponent(contractId)}/sign`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
            body: JSON.stringify({documentId, hash}),
        });
        return await readPreview(response);
    } catch (error) {
        console.error('signContract error:', error);
        return {success: false};
    }
};

export const updateContract = async (token: string, contractId: string, contractData: Contract): Promise<UpdateContractResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/update/${contractId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(contractData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.message || 'Failed to update contract',
                violations: errorData.fields || [],
            };
        }

        return {success: true};
    } catch (error) {
        console.error('updateContract error:', error);
        return {success: false, error: 'Failed to update contract'};
    }
};
